import dayjs from 'dayjs';

import type {
    DramaActor,
    DramaBadge,
    DramaCastRel,
    DramaEpisode,
    DramaItem,
    DramaListSort,
    DramaListSortDirection,
    DramaListSortField,
    DramaListDramaStatusFilter,
    DramaListStatusFilter,
    DramaRole,
    DramaStatus,
} from '@/services/dramas';

const WEB2_ASSETS_URL = import.meta.env.VITE_WEB2_ASSETS || '';

/** 列表 status 筛选：管理员审核状态 */
export const DRAMA_STATUS_FILTER_OPTIONS: DramaListStatusFilter[] = [
    'Pending_FIRST_PUBLISH',
    'Pending_ACTIVE_EDIT',
    'Pending_PASSIVE_EDIT',
    'Approved',
    'Rejected',
    'Offline',
];

const LIST_STATUS_FILTER_INTL_MAP: Record<string, string> = {
    Pending_FIRST_PUBLISH: 'dramaReview.adminReviewStatus.Pending_FIRST_PUBLISH',
    Pending_ACTIVE_EDIT: 'dramaReview.adminReviewStatus.Pending_ACTIVE_EDIT',
    Pending_PASSIVE_EDIT: 'dramaReview.adminReviewStatus.Pending_PASSIVE_EDIT',
    Approved: 'dramaReview.adminReviewStatus.Approved',
    Rejected: 'dramaReview.adminReviewStatus.Rejected',
    Offline: 'dramaReview.adminReviewStatus.Offline',
    /** 兼容旧筛选值 */
    Pending: 'dramaReview.adminReviewStatus.Pending',
};

export const dramaListStatusFilterIntlId = (status?: DramaListStatusFilter) => {
    if (!status) return 'dramaReview.adminReviewStatus.unknown';
    return LIST_STATUS_FILTER_INTL_MAP[status] ?? 'dramaReview.adminReviewStatus.unknown';
};

/** 列表 drama_status 筛选：短剧状态 */
export const DRAMA_DRAMA_STATUS_FILTER_OPTIONS: DramaListDramaStatusFilter[] = [
    'ONLINE',
    'PENDING_REVIEW',
    'REVIEW_REJECTED',
    'OFFLINE',
    'DELETED',
];

export const DEFAULT_DRAMA_DRAMA_STATUS_FILTER: DramaListDramaStatusFilter = 'ONLINE';

/** 列表页 URL 查询：drama_status=all 表示工具栏「全部」（不传 API 参数） */
export const DRAMA_STATUS_QUERY_ALL = 'all';

export const parseDramaStatusQueryParam = (
    value?: string | null,
): DramaListDramaStatusFilter | undefined => {
    const normalized = value?.trim();
    if (!normalized || normalized === DRAMA_STATUS_QUERY_ALL) return undefined;
    return DRAMA_DRAMA_STATUS_FILTER_OPTIONS.includes(normalized as DramaListDramaStatusFilter)
        ? (normalized as DramaListDramaStatusFilter)
        : undefined;
};

export const dramaListDramaStatusFilterIntlId = (status?: DramaListDramaStatusFilter) =>
    statusIntlId(status);

/** 徽章枚举 */
export const BADGE_OPTIONS: DramaBadge[] = ['OFFICIAL', 'PARTNER', 'VERIFIED', 'COMMUNITY'];

const BADGE_INTL_MAP: Record<DramaBadge, string> = {
    OFFICIAL: 'dramaReview.badge.OFFICIAL',
    PARTNER: 'dramaReview.badge.PARTNER',
    VERIFIED: 'dramaReview.badge.VERIFIED',
    COMMUNITY: 'dramaReview.badge.COMMUNITY',
};

export type { DramaBadge };

export const normalizeBadge = (badge?: DramaBadge | string | null): DramaBadge | undefined => {
    if (!badge) return undefined;
    return BADGE_OPTIONS.includes(badge as DramaBadge) ? (badge as DramaBadge) : undefined;
};

export const badgeIntlId = (badge?: DramaBadge | string | null) => {
    const normalized = normalizeBadge(badge);
    if (normalized) return BADGE_INTL_MAP[normalized];
    return 'dramaReview.badge.none';
};

const BADGE_OPTION_INTL_MAP: Record<DramaBadge, string> = {
    OFFICIAL: 'dramaReview.badge.option.drama.OFFICIAL',
    PARTNER: 'dramaReview.badge.option.drama.PARTNER',
    VERIFIED: 'dramaReview.badge.option.drama.VERIFIED',
    COMMUNITY: 'dramaReview.badge.option.drama.COMMUNITY',
};

export const badgeOptionIntlId = (badge: DramaBadge) => BADGE_OPTION_INTL_MAP[badge];

/** Figma 徽章样式（短标签 + icon） */
export const BADGE_STYLE_MAP: Record<
    DramaBadge,
    { icon: string; className: 'official' | 'community' | 'partner' | 'verified' }
> = {
    OFFICIAL: { icon: '/badge/official.svg', className: 'official' },
    COMMUNITY: { icon: '/badge/community.svg', className: 'community' },
    PARTNER: { icon: '/badge/partner.svg', className: 'partner' },
    VERIFIED: { icon: '/badge/verified.svg', className: 'verified' },
};

export const getBadgeStyle = (badge?: DramaBadge | string | null) => {
    const normalized = normalizeBadge(badge);
    return normalized ? BADGE_STYLE_MAP[normalized] : undefined;
};

/** 列表 sort 筛选：传参 sort=[{ field: direction }] */
export type { DramaListSort, DramaListSortDirection, DramaListSortField, DramaListSortItem } from '@/services/dramas';

export const DRAMA_SORT_OPTIONS: DramaListSortField[] = ['created_at', 'updated_at', 'online_at'];

export const DRAMA_SORT_DIRECTION_OPTIONS: DramaListSortDirection[] = ['desc', 'asc'];

const SORT_INTL_MAP: Record<DramaListSortField, string> = {
    created_at: 'dramaReview.sort.created_at',
    updated_at: 'dramaReview.sort.updated_at',
    online_at: 'dramaReview.sort.online_at',
};

const SORT_DIRECTION_INTL_MAP: Record<DramaListSortDirection, string> = {
    asc: 'dramaReview.sort.asc',
    desc: 'dramaReview.sort.desc',
};

export const dramaSortIntlId = (sort: DramaListSortField) => SORT_INTL_MAP[sort];
export const dramaSortDirectionIntlId = (direction: DramaListSortDirection) => SORT_DIRECTION_INTL_MAP[direction];

export const buildDramaListSort = (
    field?: DramaListSortField,
    direction: DramaListSortDirection = 'desc',
): DramaListSort | undefined => {
    if (!field) return undefined;
    return [{ [field]: direction }];
};

/** 后端枚举 → i18n key */
const STATUS_INTL_MAP: Record<string, string> = {
    PENDING_REVIEW: 'dramaReview.status.PENDING_REVIEW',
    REVIEW_REJECTED: 'dramaReview.status.REVIEW_REJECTED',
    REVIEW_APPROVED: 'dramaReview.status.REVIEW_APPROVED',
    PENDING_ONLINE: 'dramaReview.status.PENDING_ONLINE',
    ONLINE: 'dramaReview.status.ONLINE',
    OFFLINE: 'dramaReview.status.OFFLINE',
    DELETED: 'dramaReview.status.DELETED',
};

/** 兼容旧版小写状态值 */
const LEGACY_STATUS_MAP: Record<string, DramaStatus> = {
    pending: 'PENDING_REVIEW',
    approved: 'REVIEW_APPROVED',
    rejected: 'REVIEW_REJECTED',
};

export const normalizeStatus = (status?: DramaStatus): DramaStatus | undefined => {
    if (!status) return undefined;
    return (LEGACY_STATUS_MAP[status] as DramaStatus | undefined) ?? status;
};

export const resolveAssetUrl = (path?: string) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    return `${WEB2_ASSETS_URL}/${path.replace(/^\//, '')}`;
};

export const isVideoAsset = (path?: string) => {
    if (!path) return false;
    const lower = path.toLowerCase();
    return (
        lower.startsWith('video') ||
        lower.includes('.m3u8') ||
        lower.includes('.mp4') ||
        lower.includes('.mov') ||
        lower.includes('.webm')
    );
};

export type AuditStatusRecord = {
    audit_status?: DramaStatus;
    status?: DramaStatus;
};

/** 已审核通过后的正式状态 */
export const getPublishedStatus = (record?: AuditStatusRecord) =>
    normalizeStatus(record?.status ?? record?.audit_status);

/** 已删除内容不可修改徽章 */
export const canEditBadge = (record?: AuditStatusRecord) => getPublishedStatus(record) !== 'DELETED';

/** 展示/操作使用的有效状态（status，兼容 audit_status） */
export const getAuditStatus = (record?: AuditStatusRecord) => getPublishedStatus(record);

export const getAuditReason = (record?: { audit_reason?: string }) => record?.audit_reason;

export const getDramaTitle = (drama: DramaItem) => drama.title || drama.name || drama.id;

export const getEpisodeNo = (episode: DramaEpisode, index: number) =>
    episode.episode_no ?? episode.episode_number ?? episode.sort ?? index + 1;

export const getEpisodeTitle = (episode: DramaEpisode, index: number) =>
    episode.title || episode.name || `Ep.${getEpisodeNo(episode, index)}`;

/** 演员审核接口使用的 id（兼容 castRel 字段） */
export const getActorRecordId = (actor: DramaActor): string => {
    const anyActor = actor as Record<string, unknown>;
    const raw = anyActor.id ?? anyActor.actor_id ?? anyActor.actorId;
    return raw != null ? String(raw) : '';
};

export const getActorAvatar = (actor: DramaActor) => (actor as { avatar_url?: string }).avatar_url || actor.avatar;

/**
 * 取单集播放地址。
 * 列表接口对「暂无剧集」的短剧会返回 dramaEpisodes: []，此时上游可能传入 undefined，
 * 这里必须容忍空值，否则会抛 Cannot read properties of undefined (reading 'hls_url')。
 */
export const getEpisodeVideo = (episode?: DramaEpisode | null) =>
    episode?.hls_url || episode?.video_url || episode?.video;

/** 列表真实字段为 dramaEpisodes，兼容旧 episodes */
export const getDramaEpisodes = (drama: DramaItem) => {
    const episodes = (drama.dramaEpisodes?.length ? drama.dramaEpisodes : drama.episodes) ?? [];
    return [...episodes].sort((a, b) => Number(getEpisodeNo(a, 0)) - Number(getEpisodeNo(b, 0)));
};

export const findDramaEpisodeMatch = (
    episodes: DramaEpisode[],
    episodeId?: string | null,
    episodeNo?: string | number | null,
) => {
    const normalizedId = String(episodeId ?? '').trim();
    if (normalizedId) {
        const byId = episodes.find((episode) => String(episode.id) === normalizedId);
        if (byId) return byId;
    }

    const normalizedNo = String(episodeNo ?? '').trim();
    if (normalizedNo) {
        return episodes.find((episode, index) => String(getEpisodeNo(episode, index)) === normalizedNo);
    }

    return undefined;
};

export const getDramaEpisodeDomId = (episode: Pick<DramaEpisode, 'id'>) => `drama-episode-${episode.id}`;

export const sumEpisodePlayCount = (episodes: DramaEpisode[]) =>
    episodes.reduce((sum, episode) => sum + (Number(episode.play_count) || 0), 0);

/** 预览优先取首集 hls_url；无剧集时回退短剧自身 video_url（都没有则为空，由调用方渲染占位） */
export const getDramaVideo = (drama: DramaItem) => {
    const first = getDramaEpisodes(drama)[0];
    return getEpisodeVideo(first) || drama.video_url || drama.video;
};

export const getDramaHlsVideo = (drama: DramaItem) =>
    (drama as { hls_url?: string }).hls_url || getDramaEpisodes(drama)[0]?.hls_url;

export const getDramaCover = (drama: DramaItem) => drama.cover_url || drama.cover;

const TIMESTAMP_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/** 热度系数取值范围（与后端一致） */
export const HEAT_COEFFICIENT_MIN = 0;
export const HEAT_COEFFICIENT_MAX = 100000;
export const HEAT_COEFFICIENT_PRECISION = 2;

/** 输入过程中允许：整数、最多 2 位小数、可暂留末尾小数点 */
export const HEAT_COEFFICIENT_INPUT_PATTERN = /^\d*(\.\d{0,2})?$/;

/** 清洗热度系数输入：只保留数字和一个小数点，小数最多两位 */
export const sanitizeHeatCoefficientInput = (raw?: string): string => {
    if (!raw) return '';

    let value = raw.replace(/[^\d.]/g, '');
    const firstDotIndex = value.indexOf('.');
    if (firstDotIndex !== -1) {
        value =
            value.slice(0, firstDotIndex + 1) + value.slice(firstDotIndex + 1).replace(/\./g, '');
    }

    const [intPart = '', decPart = ''] = value.split('.');
    if (value.includes('.')) {
        return `${intPart}.${decPart.slice(0, HEAT_COEFFICIENT_PRECISION)}`;
    }

    return intPart;
};

export const formatHeatCoefficientInput = (value?: number | null): string => {
    if (value == null || Number.isNaN(value)) return '1';
    return String(value);
};

export const normalizeHeatCoefficientValue = (value: string | number | null | undefined): number =>
    Number(Number(value).toFixed(HEAT_COEFFICIENT_PRECISION));

export const formatHeatCoefficient = (value?: number | null) => {
    if (value == null || Number.isNaN(value)) return '-';
    return String(value);
};

/** 毫秒/秒级时间戳，或 ISO 字符串 */
export const formatDramaTimestamp = (ts?: string | number | null) => {
    if (ts == null || ts === '') return '-';

    if (typeof ts === 'string' && Number.isNaN(Number(ts))) {
        const parsed = dayjs(ts);
        return parsed.isValid() ? parsed.format(TIMESTAMP_FORMAT) : '-';
    }

    const num = Number(ts);
    if (Number.isNaN(num)) return '-';

    const ms = String(Math.trunc(num)).length === 10 ? num * 1000 : num;
    const parsed = dayjs(ms);
    return parsed.isValid() ? parsed.format(TIMESTAMP_FORMAT) : '-';
};

/** 归一化管理员审核状态（兼容旧 Pending + last_event_type） */
export const resolveAdminReviewStatus = (review?: {
    status?: string | null;
    last_event_type?: string | null;
} | null) => {
    const status = review?.status;
    if (!status) return undefined;

    if (
        status.startsWith('Pending_') ||
        status === 'Approved' ||
        status === 'Rejected' ||
        status === 'Offline'
    ) {
        return status;
    }

    if (status === 'Pending' && review?.last_event_type) {
        return `Pending_${review.last_event_type}`;
    }

    return status;
};

const ADMIN_REVIEW_STATUS_INTL_MAP: Record<string, string> = {
    Pending_FIRST_PUBLISH: 'dramaReview.adminReviewStatus.Pending_FIRST_PUBLISH',
    Pending_ACTIVE_EDIT: 'dramaReview.adminReviewStatus.Pending_ACTIVE_EDIT',
    Pending_PASSIVE_EDIT: 'dramaReview.adminReviewStatus.Pending_PASSIVE_EDIT',
    Approved: 'dramaReview.adminReviewStatus.Approved',
    Rejected: 'dramaReview.adminReviewStatus.Rejected',
    Offline: 'dramaReview.adminReviewStatus.Offline',
    Pending: 'dramaReview.adminReviewStatus.Pending',
};

export const dramaReviewStatusIntlId = (status?: string | null) => {
    if (!status) return 'dramaReview.adminReviewStatus.unknown';
    return ADMIN_REVIEW_STATUS_INTL_MAP[status] ?? 'dramaReview.adminReviewStatus.unknown';
};

export const reviewStatusTagColor = (
    status?: string | null,
): 'success' | 'processing' | 'error' | 'warning' | 'default' => {
    if (!status) return 'default';
    if (status === 'Pending' || status.startsWith('Pending_')) return 'processing';
    switch (status) {
        case 'Approved':
            return 'success';
        case 'Rejected':
            return 'error';
        case 'Offline':
            return 'default';
        default:
            return 'default';
    }
};

/** castRels 项可能是嵌套 actor，也可能是扁平演员字段 */
export const resolveCastRelActor = (rel?: DramaCastRel | DramaActor): DramaActor | undefined => {
    if (!rel) return undefined;

    const castRel = rel as DramaCastRel;
    if (castRel.actor) {
        const actorId = castRel.actor.id ?? castRel.actor_id;
        return actorId ? { ...castRel.actor, id: String(actorId) } : castRel.actor;
    }

    const actor = rel as DramaActor;
    const actorId = castRel.actor_id ?? getActorRecordId(actor);
    if (!actorId) return undefined;

    return {
        ...actor,
        id: String(actorId),
        name: actor.name ?? (actor as { nickname?: string }).nickname,
        avatar_url: actor.avatar_url ?? actor.avatar,
        status: actor.status ?? actor.audit_status,
    };
};

export const getRoleCastActors = (role: DramaRole): DramaActor[] =>
    (role.castRels ?? [])
        .map((rel) => resolveCastRelActor(rel))
        .filter((actor): actor is DramaActor => Boolean(actor?.id));

export const collectActors = (drama: DramaItem): DramaActor[] => {
    const actors: DramaActor[] = [];
    const seen = new Set<string>();

    const push = (raw: DramaActor | undefined) => {
        if (!raw?.id || seen.has(raw.id)) return;
        seen.add(raw.id);
        actors.push(raw);
    };

    drama.roles?.forEach((role) => {
        role.actors?.forEach((actor) => push(resolveCastRelActor(actor)));
        getRoleCastActors(role).forEach((actor) => push(actor));
    });

    return actors;
};

export const statusIntlId = (status?: DramaStatus) => {
    const normalized = normalizeStatus(status);
    if (normalized && STATUS_INTL_MAP[normalized]) {
        return STATUS_INTL_MAP[normalized];
    }
    return 'dramaReview.status.unknown';
};

export const statusTagColor = (status?: DramaStatus): 'success' | 'processing' | 'error' | 'warning' | 'default' => {
    switch (normalizeStatus(status)) {
        case 'PENDING_REVIEW':
            return 'processing';
        case 'REVIEW_REJECTED':
            return 'error';
        case 'REVIEW_APPROVED':
            return 'success';
        case 'PENDING_ONLINE':
            return 'warning';
        case 'ONLINE':
            return 'success';
        case 'OFFLINE':
            return 'default';
        case 'DELETED':
            return 'error';
        default:
            return 'default';
    }
};

/** 可驳回：ONLINE（事后治理）或 PENDING_REVIEW */
export const canRejectDrama = (status?: DramaStatus) => {
    const normalized = normalizeStatus(status);
    return normalized === 'ONLINE' || normalized === 'PENDING_REVIEW';
};

/** 可下架：ONLINE 或 PENDING_REVIEW → OFFLINE */
export const canOfflineDrama = (status?: DramaStatus) => {
    const normalized = normalizeStatus(status);
    return normalized === 'ONLINE' || normalized === 'PENDING_REVIEW';
};

/** 可审核通过：管理员审核状态为待审核，且短剧未下架/未删除 */
export const canApproveDrama = (record?: AuditStatusRecord & {
    review?: { status?: string | null; last_event_type?: string | null } | null;
}) => {
    const publishedStatus = getPublishedStatus(record);
    if (publishedStatus === 'OFFLINE' || publishedStatus === 'DELETED') {
        return false;
    }

    const status = resolveAdminReviewStatus(record?.review);
    return Boolean(status && (status === 'Pending' || status.startsWith('Pending_')));
};
